/**
 * Bringing a signed-in Firebase user up to a playable account.
 *
 * Every sign-in path lands here: new email/password signup, returning
 * email/password login, and Google. That matters more than it looks. `Signup`
 * and `Login` each carried their own copy of this, and the copies had already
 * drifted — one wrote `settings.lastUpdated` and the other did not, so which
 * fields your profile had depended on whether you were created before or after
 * the backfill path ran on you.
 *
 * Social sign-in is what forces the issue: there is no separate "signup" moment
 * to hang account creation off. The same button is both, and it cannot know
 * which it is until Firestore answers.
 */
import type { User } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';

import { firestore } from '../firebase';

/** Stripped of anything that would look wrong in a feed byline. */
function sanitizeName(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9]/g, '');
}

/** A name derived from an email address, for accounts that never supplied one. */
export function nameFromEmail(email: string | null | undefined): string {
  return sanitizeName(email?.split('@')[0] ?? '') || 'punster';
}

/**
 * `base`, or `base2`, or `base3`...
 *
 * Racy by construction — two people claiming the same name in the same second
 * both see it free. Display names are a label rather than an identity here
 * (`friends` and every pun key off the uid), so a collision is cosmetic, and the
 * alternative is a transaction on a shared registry document that every signup
 * in the game would queue behind.
 */
async function firstFreeName(base: string): Promise<string> {
  const taken = async (candidate: string) =>
    !(
      await getDocs(
        query(collection(firestore, 'users'), where('settings.displayName', '==', candidate)),
      )
    ).empty;

  if (!(await taken(base))) return base;
  for (let n = 1; n < 100; n += 1) {
    if (!(await taken(`${base}${n}`))) return `${base}${n}`;
  }
  return `${base}${Date.now()}`;
}

/**
 * Make sure `users/{uid}` and `userPrivate/{uid}` exist, and stamp the login.
 *
 * Safe to call on every sign-in. When the documents are already there it writes
 * `lastLogin` and nothing else — in particular it never rewrites `displayName`,
 * which the player may have changed in Settings since.
 *
 * `preferredName` is what the caller would like the display name to be: the
 * field on the signup form, or the name Google supplies. It is a starting point
 * for `firstFreeName`, not a guarantee, and it is ignored entirely for an
 * account that already exists.
 */
export async function ensureUserDocuments(user: User, preferredName?: string): Promise<void> {
  const publicRef = doc(firestore, 'users', user.uid);
  const privateRef = doc(firestore, 'userPrivate', user.uid);

  const [publicDoc, privateDoc] = await Promise.all([getDoc(publicRef), getDoc(privateRef)]);

  // Both are checked, not just the public one: accounts predating `userPrivate`
  // have a profile and no email record, and would otherwise never get one.
  if (publicDoc.exists() && privateDoc.exists()) {
    await setDoc(publicRef, { lastLogin: new Date() }, { merge: true });
    return;
  }

  const base =
    sanitizeName(preferredName?.trim() ?? '') ||
    sanitizeName(user.displayName ?? '') ||
    nameFromEmail(user.email);
  const settled = await firstFreeName(base);
  const now = new Date();

  // The email lives in userPrivate, which only its owner can read. The public
  // profile deliberately does not carry it.
  //
  // `merge` on both: this path also runs as a backfill over a half-present
  // account, where clobbering the half that exists would drop a follow list.
  await Promise.all([
    setDoc(
      publicRef,
      {
        friends: [],
        createdAt: now,
        lastLogin: now,
        displayName: settled,
        settings: {
          displayName: settled,
          displayNameLower: settled.toLowerCase(),
          lastUpdated: now.toISOString(),
        },
      },
      { merge: true },
    ),
    setDoc(privateRef, { email: user.email, createdAt: now }, { merge: true }),
  ]);
}
